<script setup lang="ts">
import type { Cart } from '@/js/types/cart.ts';
import type { Client } from '@/js/types/client.ts';
import type { DeliveryPricing, OrderRequest } from '@/js/types/order.ts';
import type { YandexSplitConfig } from '@/js/types/payment.ts';
import { createOrder } from '@/js/api/order.ts';
import { GOALS, reachGoal, reachGoalAsync } from '@/js/goal.ts';
import { getLocaleValue } from '@/js/helper/locale.ts';
import { ERROR_REQUEST_TEXT, showErrorNotify } from '@/js/notification.ts';
import { getPhonePlaceholder } from '@/js/phone-maska.ts';
import { classname, formatPrice } from '@/js/utils';
import BaseButton from '@/js/vue/base/base-button.vue';
import BaseCheckbox from '@/js/vue/base/base-checkbox.vue';
import AppHint from '@/js/vue/base/base-hint.vue';
import BaseIcon from '@/js/vue/base/base-icon.vue';
import BaseInputRadio from '@/js/vue/base/base-input-radio.vue';
import BaseInput from '@/js/vue/base/base-input.vue';
import AppLoader from '@/js/vue/base/base-loader.vue';
import BaseSelect from '@/js/vue/base/base-select.vue';
import BaseInputTel from '@/js/vue/base/base-tel.vue';
import AppAdditionalService from '@/js/vue/checkout/additional-service.vue';
import AppCallManager from '@/js/vue/checkout/call-manager.vue';
import {
  cleanOrderFields,
  getCallTimes,
  getCourierDeliveryValidationError,
  getObtainingMethods,
  getPaymentMethods,
} from '@/js/vue/checkout/data.ts';
import DeliveryAddressModal from '@/js/vue/checkout/delivery-address-modal.vue';
import AppOrderDetails from '@/js/vue/checkout/order-details.vue';
import ProductCost from '@/js/vue/checkout/product-cost.vue';
import ProductService from '@/js/vue/checkout/product-service.vue';
import YandexSplitWidget from '@/js/vue/checkout/yandex-split-widget.vue';
import { useCartTotal } from '@/js/vue/composable/cart-total.ts';
import { useTotals } from '@/js/vue/composable/totals.ts';
import { useMediaQuery } from '@vueuse/core';
import { computed, onMounted, ref, watch } from 'vue';

const props = defineProps<{
  deliveryPricing: DeliveryPricing
  client: Client | null
  cart: Cart | null
  pickupPrice: number
  pickupAddress: string
  yandexSplit: YandexSplitConfig | null
}>();

const classCheckout = classname('checkout');
const isMobile = useMediaQuery('(max-width: 991px)');

const { products, totals, setCartTotal } = useCartTotal();
watch(() => props.cart, (data) => {
  if (data) {
    setCartTotal(data);
  }
}, { immediate: true });

// Шаг 3 воронки «Оформление корзины» — страница оформления открыта с товарами.
onMounted(() => {
  if (products.value.length > 0) {
    reachGoal(GOALS.CHECKOUT);
  }
});

// Order
const orderData = ref<OrderRequest>({
  name: '',
  email: '',
  phone: '',

  legal_type: 'individual',
  legal_data: {
    inn: null,
  },

  delivery_type: 'pickup',
  delivery_data: {
    address: '',
    distance: 0,
    price: 0,
    response: [],
  },

  payment_method: 'transfer',
  payment_data: {
    coupon: null,
    bonus: null,
  },

  additional_data: {
    call_time: '',
  },

  services: {
    projecting: false,
    installment_items: [],
  },
});

watch(() => props.client, (user) => {
  if (user) {
    orderData.value.name = `${user.firstName || ''}`;
    orderData.value.email = user.email;
    orderData.value.phone = user.phone;
  }
}, {
  immediate: true,
});
// errors
const errors = ref<string[]>([]);

function clearErrors() {
  errors.value = [];
}

watch(orderData, clearErrors, { deep: true });

// Payments
const isSplitAvailable = ref(true);
const paymentMethods = computed(() => getPaymentMethods()
  .filter(method => method.value !== 'yandex_split' || isSplitAvailable.value));
watch(isSplitAvailable, (available) => {
  if (!available && orderData.value.payment_method === 'yandex_split') {
    orderData.value.payment_method = paymentMethods.value[0].value;
  }
});

// Delivery
const obtainingMethods = computed(() => getObtainingMethods());
const deliveryType = computed(() => orderData.value.delivery_type);
const isPickup = computed(() => orderData.value.delivery_type === 'pickup');
const isCourier = computed(() => orderData.value.delivery_type === 'self');
const isAddressModalOpen = ref(false);
const selectedAddress = computed(() => orderData.value.delivery_data.address);
const isInsideDelivery = ref(false);
const isOutsideDelivery = ref(false);
watch([isInsideDelivery, isOutsideDelivery], () => {
  errors.value = errors.value.filter(error => error !== 'address');
});
watch(() => orderData.value.delivery_type, (type) => {
  if (type === 'pickup' || type === 'tk_terminal') {
    orderData.value.delivery_data.address = '';
    orderData.value.delivery_data.distance = 0;
    isInsideDelivery.value = false;
    isOutsideDelivery.value = false;
    isAddressModalOpen.value = false;
  }
});
const callTimes = computed(() => getCallTimes());
const isQuestions = ref(false);

const isLoading = ref(false);
const isOrderAdded = ref(false);
const isPrivacyAccepted = ref(false);
const isSubscribed = ref(false);
watch(isSubscribed, (val) => {
  if (orderData.value.additional_data) {
    orderData.value.additional_data.is_subscribed = val;
  }
});

function validateCourierDelivery(): boolean {
  if (!isCourier.value) {
    return true;
  }
  const error = getCourierDeliveryValidationError(
    orderData.value.delivery_data.address,
    isInsideDelivery.value,
    isOutsideDelivery.value,
  );
  if (error) {
    errors.value = ['address'];
    showErrorNotify({ text: error });
    isAddressModalOpen.value = true;
    return false;
  }
  return true;
}

async function addOrder() {
  if (!validateCourierDelivery()) {
    return;
  }
  try {
    isLoading.value = true;
    const result = await createOrder(cleanOrderFields(orderData.value));
    isOrderAdded.value = result.data?.success ?? false;
    if (result.data?.success && result.data.payment_url) {
      // Шаг 4 воронки «Оформление корзины» — заказ создан на стороне сервера.
      await reachGoalAsync(GOALS.SEND);
      window.location.href = result.data.payment_url;
    }
    else {
      showErrorNotify({ text: ERROR_REQUEST_TEXT });
    }
  }
  catch (e) {
    console.log(e);
    showErrorNotify({ text: ERROR_REQUEST_TEXT });
  }
  finally {
    isLoading.value = false;
  }
}

// Installment
const isInstallationNeeded = ref(false);
const installmentProducts = computed(
  () =>
    products.value.filter(
      product => product.installmentPrice && product.installmentPrice > 0,
    ),
);
const installationProductIds = ref<number[]>([]);
watch(isInstallationNeeded, (needed) => {
  if (!needed) {
    installationProductIds.value = [];
  }
});
watch(installationProductIds, (ids) => {
  if (orderData.value.services) {
    orderData.value.services.installment_items = installmentProducts.value
      .filter(product => ids.includes(product.productId))
      .map(item => ({
        product_id: item.productId,
        price: item.installmentPrice ?? 0,
      }));
  }
});

// Totals
const installmentPrice = computed(() => orderData.value.services?.installment_items.reduce((acc, curr) => acc + curr.price, 0) ?? 0);
const { totalQuantity, finalAmount, finalPrice, orderTotals, deliveryTotals } = useTotals({
  deliveryPricing: props.deliveryPricing,
  products,
  cartTotals: totals,
  installmentPrice,
  distance: computed(() => orderData.value.delivery_data.distance),
  isOutsideDelivery,
  isInsideDelivery,
  pickupPrice: props.pickupPrice,
  deliveryType,
});
watch(deliveryTotals, (delivery) => {
  orderData.value.delivery_data.price = delivery.length > 0
    ? Number(delivery[0].value)
    : 0;
}, { immediate: true });
</script>

<template>
  <form :class="classCheckout()" @submit.prevent="addOrder">
    <div :class="classCheckout('col')">
      <!--      ваши данные -->
      <AppOrderDetails>
        <template #title>
          {{ getLocaleValue('account_profile') }}
        </template>
        <template #content>
          <div :class="classCheckout('your-data-inputs')">
            <BaseInputRadio
              v-model="orderData.legal_type"
              size="sm"
              value="individual"
              name="person"
              required
            >
              {{ getLocaleValue('individual') }}
            </BaseInputRadio>
            <BaseInputRadio
              v-model="orderData.legal_type"
              size="sm"
              value="organization"
              name="person"
              required
            >
              {{ getLocaleValue('legal_entity') }}
            </BaseInputRadio>
          </div>
          <div :class="classCheckout('phone-container', { error: errors.includes('phone') })">
            <BaseInputTel
              v-model="orderData.phone"
              :placeholder="getPhonePlaceholder()"
              required
            />
            <span v-html="getLocaleValue('enter_your_phone_number')" />
          </div>
          <BaseInput v-model="orderData.name" required :placeholder="getLocaleValue('full_name')" />
          <BaseInput
            v-model="orderData.email"
            type="email"
            required
            :placeholder="getLocaleValue('e_mail')"
          />
          <div
            v-if="orderData.legal_type === 'organization'"
            :class="classCheckout('company')"
          >
            <div :class="classCheckout('company-title')">
              {{ getLocaleValue('organization_details') }}
            </div>
            <BaseInput
              v-if="orderData.legal_data"
              v-model="orderData.legal_data.inn"
              type="number"
              :placeholder="getLocaleValue('inn_organization')"
              required
            />
          </div>
        </template>
      </AppOrderDetails>
      <AppOrderDetails v-if="installmentProducts.length > 0" size="large">
        <template #title>
          {{ getLocaleValue('additional_services') }}
        </template>
        <template #content>
          <AppAdditionalService v-if="orderData.services" v-model="orderData.services.projecting">
            <template #icon>
              <BaseIcon icon="ruler" type="main" :width="40" :height="40" />
            </template>
            <template #title>
              {{ getLocaleValue('planning_title') }}
            </template>
            <template #desc>
              {{ getLocaleValue('we_will_make_a_project') }}
            </template>
          </AppAdditionalService>
          <AppAdditionalService v-model="isInstallationNeeded">
            <template #icon>
              <BaseIcon icon="tools" type="main" :width="40" :height="40" />
            </template>
            <template #title>
              {{ getLocaleValue('installation') }}
            </template>
            <template #desc>
              {{ getLocaleValue('we_will_carry_out_assembly') }}
            </template>
          </AppAdditionalService>
          <div v-if="isInstallationNeeded">
            <ProductService
              v-for="product in installmentProducts"
              :key="product.id"
              v-model:product-ids="installationProductIds"
              :product-id="product.productId"
              :image="product.productImage.url ?? ''"
              :name="product.productName"
              :price="formatPrice(product.installmentPrice ?? 0)"
              url="/montazh"
            />
          </div>
          <AppHint :class="classCheckout('hint')">
            {{ getLocaleValue('the_manager_will_contact_you') }}
          </AppHint>
        </template>
      </AppOrderDetails>
      <!--      способ получения -->
      <AppOrderDetails>
        <template #title>
          {{ getLocaleValue('method_of_obtaining') }}
        </template>
        <template #content>
          <div :class="classCheckout('obtaining-toggle')">
            <BaseInputRadio
              v-for="item in obtainingMethods"
              :key="item.value"
              v-model="orderData.delivery_type"
              name="obtaining_method"
              :value="item.value"
              size="sm"
            >
              {{ item.label }}
            </BaseInputRadio>
          </div>
          <p
            v-if="isPickup && pickupAddress"
            :class="classCheckout('pickup-address')"
          >
            {{ pickupAddress }}
          </p>
          <div v-if="isCourier" :class="classCheckout('address-select', { error: errors.includes('address') })">
            <BaseButton
              type="button"
              theme="primary"
              @click="isAddressModalOpen = true"
            >
              {{ selectedAddress || 'Выберите адрес доставки' }}
            </BaseButton>
            <p
              v-if="errors.includes('address')"
              :class="classCheckout('address-error')"
            >
              {{ getCourierDeliveryValidationError(selectedAddress, isInsideDelivery, isOutsideDelivery) }}
            </p>
          </div>
        </template>
      </AppOrderDetails>
      <DeliveryAddressModal
        v-if="isCourier"
        v-model:shown="isAddressModalOpen"
        v-model:address="orderData.delivery_data.address"
        v-model:is-inside-delivery="isInsideDelivery"
        v-model:is-outside-delivery="isOutsideDelivery"
        v-model:distance="orderData.delivery_data.distance"
      />
      <!--      способ оплаты -->
      <AppOrderDetails>
        <template #title>
          {{ getLocaleValue('payment_method') }}
        </template>
        <template #content>
          <BaseInputRadio
            v-for="(item) in paymentMethods"
            :key="item.value"
            v-model="orderData.payment_method"
            name="payment"
            :value="item.value"
            required
            size="sm"
          >
            {{ item.label }}
          </BaseInputRadio>
          <YandexSplitWidget
            v-if="yandexSplit?.merchant_id"
            :class="classCheckout('split-widget')"
            :merchant-id="yandexSplit.merchant_id"
            :sandbox="yandexSplit.sandbox"
            :amount="finalAmount"
            @availability="isSplitAvailable = $event"
          />
        </template>
      </AppOrderDetails>
      <div :class="classCheckout('checkboxes-mobile')">
        <BaseCheckbox v-model="isPrivacyAccepted" :required="isMobile">
          <span v-html="getLocaleValue('privacy_policy')" />
        </BaseCheckbox>
        <BaseCheckbox v-model="isSubscribed" :value="1">
          <span v-html="getLocaleValue('page_subscribe')" />
        </BaseCheckbox>
      </div>
      <!-- у меня остались вопросы -->
      <div :class="classCheckout('questions')">
        <AppOrderDetails :is-heading="false">
          <template #content>
            <AppCallManager v-model="isQuestions" />
            <BaseSelect
              v-if="isQuestions && orderData.additional_data"
              v-model="orderData.additional_data.call_time"
              :options="callTimes"
              :placeholder="getLocaleValue('select_time')"
            >
              {{ getLocaleValue('when_should_i_call_you') }}
            </BaseSelect>
          </template>
        </AppOrderDetails>
      </div>

      <div
        v-show="!isOrderAdded"
        :class="classCheckout('submit-mobile')"
      >
        <BaseButton :disabled="isLoading" type="submit" theme="primary">
          {{ getLocaleValue('place_an_order') }}
        </BaseButton>
      </div>
    </div>
    <div :class="classCheckout('col')">
      <div :class="classCheckout('product-cost-wrapper')">
        <div :class="classCheckout('product-cost')">
          <ProductCost
            :class="classCheckout('cost')"
            :totals="orderTotals"
            :final-price
          />
          <div :class="classCheckout('button-wrapper')">
            <div
              v-show="!isOrderAdded"
              :class="classCheckout('button-container')"
            >
              <div :class="classCheckout('quantity-text')">
                {{ totalQuantity }}
              </div>
              <div :class="classCheckout('total-price')">
                {{ finalPrice }}
              </div>
              <BaseButton :disabled="isLoading" type="submit" theme="accent" :class="classCheckout('submit')">
                {{ getLocaleValue('place_an_order') }}
              </BaseButton>
              <div :class="classCheckout('checkboxes')">
                <BaseCheckbox v-model="isPrivacyAccepted" :required="!isMobile">
                  <span v-html="getLocaleValue('privacy_policy')" />
                </BaseCheckbox>
                <BaseCheckbox v-model="isSubscribed" :value="1">
                  <span v-html="getLocaleValue('page_subscribe')" />
                </BaseCheckbox>
              </div>
            </div>
            <AppLoader v-show="isOrderAdded" :class="classCheckout('loader')" />
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<style lang="scss">
.checkout {
  display: grid;
  grid-gap: 140px;
  grid-template-columns: 4fr 3fr;

  &__obtaining-toggle {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  &__pickup-address {
    margin: 0;
    font-size: 16px;
    line-height: 1.4;
  }

  &__split-widget {
    margin-top: 12px;
  }

  &__address-select {
    display: grid;
    gap: 8px;

    &_error {
      .base-button {
        border-color: #e53935;
      }
    }
  }

  &__address-error {
    margin: 0;
    color: #e53935;
    font-size: 14px;
  }

  &__col {
    display: grid;
    grid-gap: 40px;
  }

  &__product-cost {
    position: sticky;
    top: 20px;
    margin-top: 20px;
  }

  &__your-data-inputs {
    display: grid;
    grid-gap: 10px;
    grid-template-columns: repeat(2, auto);
    max-width: 400px;
  }

  &__company {
    display: grid;
    grid-gap: 26px;
    padding-top: 26px;
    border-top: 1px solid #979797;
  }

  &__company-title {
    font-size: 24px;
  }

  &__button-wrapper {
    display: grid;
    grid-gap: 20px;
    margin-top: 30px;
  }

  &__quantity-text,
  &__total-price {
    display: none;
  }

  &__loader {
    margin-inline: auto;
  }

  &__checkboxes {
    display: grid;
    grid-gap: 8px;
    margin: 24px 0 8px 0;
  }

  &__checkboxes-mobile {
    display: none;
  }

  &__submit-mobile {
    display: none;
  }

  &__phone-container {
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    color: #2779cf;

    input {
      box-sizing: border-box;
      width: 100%;
      padding: 22px 14px;
      font-size: 20px;
      line-height: 1.15;
      border: 1px solid #2779cf;
      border-radius: 2px;
      outline: none;

      &::placeholder {
        color: var(--base-input-tel-color);
      }

      &:not(:read-only, .error) {
        &:hover,
        &:focus {
          border-color: #000;
        }
      }

      &:not(:read-only, .error) {
        &:hover,
        &:focus {
          border-color: #2779cf;
        }
      }
    }

    &.error {
      input {
        border-color: red;
      }
    }
  }

  @media screen and (max-width: 1200px) {
    grid-gap: 50px;
  }

  @media screen and (max-width: 991px) {
    grid-template-columns: 1fr;
    gap: 0;

    &__obtaining-toggle {
      grid-template-columns: 1fr;
    }

    &__company {
      gap: 16px;
      margin-top: 16px;
      padding-top: 0;
      border-top: none;
    }

    &__company-title {
      font-weight: 500;
      font-size: 16px;
    }

    //    TODO: mm-page mm-slideout ломают стили
    &__button-wrapper {
      position: fixed;
      z-index: 11;
      right: 0;
      bottom: calc(env(safe-area-inset-bottom, 0) + 50px);
      left: 0;
      margin-top: 0;
      padding: 8px;
      background-color: #fff;
      box-shadow: 0 5px 15px #000;
    }

    &__button-container {
      display: grid;
      grid-template-areas: 'text price' 'agreement agreement' 'button button';
      gap: 8px;
      align-items: center;
    }

    &__checkboxes {
      grid-area: agreement;
      margin-top: 0;
    }

    &__checkboxes-mobile {
      display: grid;
      grid-gap: 8px;
    }

    &__quantity-text {
      grid-area: text;
      color: #898989;
      font-size: 14px;
      font-weight: 400;
    }

    &__submit {
      grid-area: button;
    }

    &__total-price {
      grid-area: price;
      font-size: 20px;
      font-weight: 700;
      text-align: right;
    }

    &__quantity-text,
    &__total-price {
      display: block;
    }

    &__submit-mobile {
      display: block;
      margin-top: 20px;
    }

    &__product-cost {
      position: unset;
    }

    &__uds,
    &__cost,
    &__questions {
      display: none;
    }

    &__phone-container {
      input {
        padding: 14px 24px;
        font-size: 16px;
      }

      &__info {
        display: none;
      }
    }
  }

  @media screen and (max-width: 768px) {
    &__col {
      grid-gap: 20px;
    }
  }
}
</style>
